/**
 * Industry Template Service
 * Handles loading, applying, and managing industry-specific templates
 */

import { IndustryTemplate, TemplateApplicationResult } from '../types/industryTemplates';
import { BusinessPlan, SectionId } from '../types/businessPlan';
import { supabase } from '../lib/supabaseClient';
import { ALL_TEMPLATES_500, TEMPLATE_STATS } from '../data/allTemplates500';
import { getTemplateById } from '../data/industryTemplateData';

/**
 * Get all available industry templates
 * First tries to fetch from database, falls back to local data
 */
export async function getAllTemplates(): Promise<IndustryTemplate[]> {
  try {
    const { data, error } = await supabase
      .from('industry_templates')
      .select('*')
      .eq('is_active', true)
      .order('popularity', { ascending: false });
    
    if (error || !data || data.length === 0) {
      console.log(`Using local template data: ${TEMPLATE_STATS.total} templates`);
      return ALL_TEMPLATES_500;
    }
    
    // Transform database format to TypeScript format
    return data.map(dbTemplate => ({
      id: dbTemplate.id,
      name: dbTemplate.name,
      category: dbTemplate.category,
      description: dbTemplate.description,
      icon: dbTemplate.icon,
      sectionTemplates: dbTemplate.section_templates,
      smartPrompts: dbTemplate.smart_prompts,
      financialDefaults: dbTemplate.financial_defaults,
      keyMetrics: dbTemplate.key_metrics,
      commonChallenges: dbTemplate.common_challenges,
      popularity: dbTemplate.popularity,
      isActive: dbTemplate.is_active,
      created_at: dbTemplate.created_at,
      updated_at: dbTemplate.updated_at
    }));
  } catch (error) {
    console.error('Error fetching templates:', error);
    return ALL_TEMPLATES_500;
  }
}

/**
 * Get a specific template by ID
 */
export async function getTemplate(templateId: string): Promise<IndustryTemplate | null> {
  try {
    // Try database first
    const { data, error } = await supabase
      .from('industry_templates')
      .select('*')
      .eq('id', templateId)
      .single();
    
    if (error || !data) {
      // Fall back to local data
      return getTemplateById(templateId) || null;
    }
    
    return {
      id: data.id,
      name: data.name,
      category: data.category,
      description: data.description,
      icon: data.icon,
      sectionTemplates: data.section_templates,
      smartPrompts: data.smart_prompts,
      financialDefaults: data.financial_defaults,
      keyMetrics: data.key_metrics,
      commonChallenges: data.common_challenges,
      popularity: data.popularity,
      isActive: data.is_active,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  } catch (error) {
    console.error('Error fetching template:', error);
    return getTemplateById(templateId) || null;
  }
}

/**
 * Apply a template to a business plan
 * This merges template content with existing plan data without overwriting user content
 */
export function applyTemplateToBusinessPlan(
  plan: BusinessPlan,
  template: IndustryTemplate,
  options: {
    overwriteExisting?: boolean;
    sectionsToApply?: SectionId[];
  } = {}
): TemplateApplicationResult {
  const { overwriteExisting = false, sectionsToApply } = options;
  
  const appliedSections: SectionId[] = [];
  const prefilledFields: string[] = [];
  
  try {
    // Determine which sections to apply
    const targetSections = sectionsToApply || Object.keys(template.sectionTemplates) as SectionId[];
    
    targetSections.forEach(sectionId => {
      const sectionTemplate = template.sectionTemplates[sectionId];
      if (!sectionTemplate) return;
      
      const existingSection = plan.sections[sectionId];
      
      // Only apply if section is empty or overwrite is enabled
      const shouldApply = overwriteExisting || 
                         !existingSection?.content || 
                         existingSection.content.trim().length === 0;
      
      if (shouldApply) {
        // Update the section with template content
        plan.sections[sectionId] = {
          ...existingSection,
          content: sectionTemplate.templateContent,
          status: 'not-started'
        };
        
        appliedSections.push(sectionId);
        prefilledFields.push(...sectionTemplate.requiredFields);
      }
    });
    
    return {
      success: true,
      appliedSections,
      prefillledFields: prefilledFields,
      message: `Applied ${appliedSections.length} section templates from ${template.name}`
    };
  } catch (error) {
    console.error('Error applying template:', error);
    return {
      success: false,
      appliedSections: [],
      prefillledFields: [],
      message: 'Failed to apply template'
    };
  }
}

/**
 * Record that a template was used for a business plan
 */
export async function recordTemplateUsage(
  businessPlanId: string,
  templateId: string,
  userId: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('business_plan_templates')
      .insert({
        business_plan_id: businessPlanId,
        template_id: templateId,
        user_id: userId,
        customizations: []
      });
    
    if (error) {
      console.error('Error recording template usage:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error recording template usage:', error);
    return false;
  }
}

/**
 * Get template usage for a business plan
 */
export async function getTemplateUsage(businessPlanId: string): Promise<{
  template: IndustryTemplate | null;
  appliedAt: string | null;
} | null> {
  try {
    const { data, error } = await supabase
      .from('business_plan_templates')
      .select('template_id, applied_at')
      .eq('business_plan_id', businessPlanId)
      .single();
    
    if (error || !data) {
      return null;
    }
    
    const template = await getTemplate(data.template_id);
    
    return {
      template,
      appliedAt: data.applied_at
    };
  } catch (error) {
    console.error('Error fetching template usage:', error);
    return null;
  }
}

/**
 * Generate smart prompts for a section based on industry template
 */
export function getSmartPromptsForSection(
  template: IndustryTemplate,
  sectionId: SectionId
): string[] {
  const editingHints = template.smartPrompts.editingHints?.[sectionId] || [];
  const sectionTemplate = template.sectionTemplates[sectionId];
  const guidancePrompts = sectionTemplate?.guidancePrompts || [];
  
  return [...editingHints, ...guidancePrompts];
}

/**
 * Get financial defaults from template
 */
export function getFinancialDefaultsFromTemplate(template: IndustryTemplate) {
  return template.financialDefaults;
}

/**
 * Get key metrics for industry
 */
export function getKeyMetricsForIndustry(template: IndustryTemplate): string[] {
  return template.keyMetrics;
}

/**
 * Replace placeholders in template content with actual values
 */
export function fillTemplatePlaceholders(
  templateContent: string,
  values: Record<string, string>
): string {
  let filledContent = templateContent;
  
  Object.entries(values).forEach(([key, value]) => {
    const placeholder = `[${key}]`;
    filledContent = filledContent.replace(new RegExp(placeholder, 'g'), value);
  });
  
  return filledContent;
}

/**
 * Validate if template can be applied to plan
 */
export function canApplyTemplate(plan: BusinessPlan): {
  canApply: boolean;
  reason?: string;
} {
  // Check if plan already has significant content
  const hasContent = Object.values(plan.sections).some(
    section => section.content && section.content.trim().length > 100
  );
  
  if (hasContent) {
    return {
      canApply: true,
      reason: 'Plan has existing content. Template will only fill empty sections.'
    };
  }
  
  return { canApply: true };
}

/**
 * Get template recommendations based on business idea
 */
export function getTemplateRecommendations(
  businessIdea: {
    title?: string;
    description?: string;
    industry?: string;
  }
): IndustryTemplate[] {
  const allTemplates = ALL_TEMPLATES_500;
  
  // If industry is specified, prioritize matching templates
  if (businessIdea.industry) {
    const industryLower = businessIdea.industry.toLowerCase();
    const matchingTemplates = allTemplates.filter(t => 
      t.name.toLowerCase().includes(industryLower) ||
      t.category.toLowerCase().includes(industryLower)
    );
    
    if (matchingTemplates.length > 0) {
      return matchingTemplates;
    }
  }
  
  // Otherwise, return most popular templates
  return [...allTemplates]
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, 6);
}
